import React, { createContext, useContext, useState, ReactNode } from 'react';

export interface ALMAAnalysisResults {
  infectedRbcCount: number;
  uninfectedRbcCount: number;
  totalCells: number;
  parasitemiaPercent: number;
  positiveBoxes?: any[];
  negativeBoxes?: any[];
}

export interface ALMAWorkflowCallbacks {
  onLaunchAnalysis?: () => void;
  onNewAnalysis?: () => void;
  onViewRecentCases?: () => void;
  onSelectCase?: (caseId: string) => void;
}

export interface ALMAWorkflowState {
  currentView?: 'recent-cases' | 'new-analysis' | 'case-details';
  hasUploadedImages?: boolean;
  imagesCount?: number;
  isAnalyzing?: boolean;
  hasResults?: boolean;
}

export interface BlotAnalysisResults {
  total_blots: number;
  total_bands: number;
  processing_time_seconds: number;
  pathology_id?: string;
  pathology_name?: string;
  sample_id?: string;
}

export interface BlotWorkflowCallbacks {
  onLaunchAnalysis?: () => void;
  onNewAnalysis?: () => void;
  onViewRecentCases?: () => void;
  onSelectCase?: (caseId: string) => void;
}

export interface BlotWorkflowState {
  currentView?: 'recent-cases' | 'new-analysis' | 'case-details';
  hasUploadedFile?: boolean;
  isAnalyzing?: boolean;
  hasResults?: boolean;
  selectedPathology?: {
    id: string;
    name: string;
    color: string;
  };
}

export interface ImmunoblotGuide {
  pathologyName: string;
  performance?: {
    sensitivity: string;
    sensitivityCI?: string;
    specificity: string;
    specificityCI?: string;
  };
  bands: {
    specific: Array<{ name: string; molecularWeight: number; required: boolean }>;
    nonSpecific?: Array<{ name: string; molecularWeight: number }>;
    primaryZone: string;
  };
  interpretation: {
    positive: string;
    equivocal: string;
    negative: string;
  };
  qualityControl?: {
    positiveControl: string;
    negativeControl: string;
  };
}

export interface AIChatContextData {
  type: 'article' | 'project' | 'general' | 'alma' | 'blot';
  articleId?: string;
  projectId?: string;
  articleTitle?: string;
  projectName?: string;
  articleCount?: number;
  sourceCount?: number;
  // ALMA context
  slideId?: string;
  analysisResults?: ALMAAnalysisResults;
  availableActions?: string[];
  almaWorkflowCallbacks?: ALMAWorkflowCallbacks;
  almaWorkflowState?: ALMAWorkflowState;
  // Blot context
  blotAnalysisResults?: BlotAnalysisResults;
  blotWorkflowCallbacks?: BlotWorkflowCallbacks;
  blotWorkflowState?: BlotWorkflowState;
  // Immunoblot guide for auto-opening chat
  immunoblotGuide?: ImmunoblotGuide;
}

interface AIChatContextType {
  context: AIChatContextData;
  setContext: (context: AIChatContextData) => void;
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  pendingGuideMessage: string | null;
  clearPendingGuide: () => void;
  openWithGuide: (guide: ImmunoblotGuide) => void;
}

const AIChatContext = createContext<AIChatContextType | undefined>(undefined);

const formatGuideMessage = (guide: ImmunoblotGuide): string => {
  let message = `🧬 **Guide d'analyse - ${guide.pathologyName}**\n\n`;

  // Performance
  if (guide.performance) {
    message += `📊 **Performances du test:**\n`;
    message += `• Sensibilité: **${guide.performance.sensitivity}**`;
    if (guide.performance.sensitivityCI) message += ` (IC: ${guide.performance.sensitivityCI})`;
    message += `\n• Spécificité: **${guide.performance.specificity}**`;
    if (guide.performance.specificityCI) message += ` (IC: ${guide.performance.specificityCI})`;
    message += `\n\n`;
  }

  // Bands to identify
  message += `🔬 **Bandes à identifier (${guide.bands.primaryZone}):**\n`;
  guide.bands.specific.forEach(band => {
    const required = band.required ? ' ⚠️ **OBLIGATOIRE**' : '';
    message += `• ${band.name} (${band.molecularWeight} kDa)${required}\n`;
  });
  if (guide.bands.nonSpecific && guide.bands.nonSpecific.length > 0) {
    message += `\n⚠️ **Bandes NON spécifiques (à ignorer):**\n`;
    guide.bands.nonSpecific.forEach(band => {
      message += `• ${band.name} (${band.molecularWeight} kDa)\n`;
    });
  }
  message += `\n`;

  // Interpretation
  message += `📋 **Critères d'interprétation:**\n`;
  message += `✅ **POSITIF:** ${guide.interpretation.positive}\n`;
  message += `❌ **NÉGATIF:** ${guide.interpretation.negative}\n\n`;

  // Quality control
  if (guide.qualityControl) {
    message += `🔍 **Contrôle qualité:**\n`;
    message += `• Témoin positif: ${guide.qualityControl.positiveControl}\n`;
    message += `• Témoin négatif: ${guide.qualityControl.negativeControl}\n\n`;
  }

  message += `---\nJe suis là pour vous aider dans votre analyse. N'hésitez pas à me poser des questions!`;

  return message;
};

export const AIChatProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [context, setContext] = useState<AIChatContextData>({
    type: 'general',
  });
  const [isOpen, setIsOpen] = useState(false);
  const [pendingGuideMessage, setPendingGuideMessage] = useState<string | null>(null);

  const clearPendingGuide = () => setPendingGuideMessage(null);

  const openWithGuide = (guide: ImmunoblotGuide) => {
    const message = formatGuideMessage(guide);
    setPendingGuideMessage(message);
    setIsOpen(true);
  };

  return (
    <AIChatContext.Provider value={{
      context,
      setContext,
      isOpen,
      setIsOpen,
      pendingGuideMessage,
      clearPendingGuide,
      openWithGuide
    }}>
      {children}
    </AIChatContext.Provider>
  );
};

export const useAIChat = () => {
  const context = useContext(AIChatContext);
  if (!context) {
    throw new Error('useAIChat must be used within AIChatProvider');
  }
  return context;
};
